import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { TextInput, ReactSelectInput } from '@hivebrite/design-system/Inputs';

import LocationSearch from 'hb-react/frontoffice/apps/Mentoring/components/LocationSearch';
import {
  type CustomAttributeType,
  type Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { getLowerCaseMentorLabel } from 'hb-react/frontoffice/apps/Mentoring/utils';
import {
  type KeywordFilterReturn,
  type LocationFilterReturn,
  type SkillsFilterReturn,
} from 'hb-react/frontoffice/apps/Mentoring/utils/types';
import { ReactSelectSkills } from 'hb-react/shared/components/DesignSystem/Inputs/ReactSelect/Async';

interface FiltersParams {
  attributes: Array<CustomAttributeType>;
  config: Program['config'];
  mentorLabel: string;
}

export const makeFilters = ({
  mentorLabel,
  config,
  attributes,
}: FiltersParams) => {
  const {
    is_keyword_displayed_in_search_filters: isKeywordDisplayedInSearchFilters,
    is_location_displayed_in_search_filters: isLocationDisplayedInSearchFilters,
    is_skills_displayed_in_search_filters: isSkillsDisplayedInSearchFilters,
  } = config;

  const customAttributesFilters = attributes.map(
    ({ name, placeholder, display_name: displayName, options }) => ({
      id: name,
      component: ({ id, value, onChange }) => (
        <ReactSelectInput
          id={id}
          name={id}
          placeholder={placeholder}
          label={displayName}
          options={options?.map((option) => ({
            label: option,
            value: option,
          }))}
          onChange={onChange}
          value={value ?? null}
          isMulti
          isSearchable
        />
      ),
    }),
  );

  return [
    isKeywordDisplayedInSearchFilters && {
      id: 'keyword',
      component: ({ id, value, onChange }: KeywordFilterReturn) => (
        <TextInput
          clearable
          icon={() => (
            <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
          )}
          id={id}
          name={id}
          label={s__('Mentoring|Search for a %{label}').i__({
            label: getLowerCaseMentorLabel(mentorLabel),
          })}
          onChange={(e) => onChange(e.target.value)}
          placeholder={s__('Mentoring|Type a name')}
          value={value ?? ''}
        />
      ),
    },
    isLocationDisplayedInSearchFilters && {
      id: 'location',
      component: ({ id, value, onChange }: LocationFilterReturn) => (
        <LocationSearch id={id} value={value} onChange={onChange} />
      ),
    },
    isSkillsDisplayedInSearchFilters && {
      id: 'skills',
      component: ({ id, value, onChange }: SkillsFilterReturn) => (
        <ReactSelectSkills
          id={id}
          name={id}
          placeholder={s__('Mentoring|Skills')}
          label={s__('Mentoring|Search by skills')}
          onChange={onChange}
          value={value ?? null}
          isMulti
          isSearchable
        />
      ),
    },
    ...customAttributesFilters,
  ].filter(Boolean);
};
